import React, { ReactNode } from 'react';
import {
  Dimensions,
  KeyboardAvoidingView,
  Modal,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { Appbar, useTheme } from 'react-native-paper';

const { height: SCREEN_HEIGHT } = Dimensions.get('window');

interface BaseBottomSheetProps {
  visible: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  height?: number; // Percentage of screen height (0.5 = 50%, 0.7 = 70%)
}

const BaseBottomSheet: React.FC<BaseBottomSheetProps> = ({
  visible,
  onClose,
  title,
  children,
  height = 0.6, // Default to 60% of screen height
}) => {
  const theme = useTheme();

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <TouchableOpacity
          style={styles.backdrop}
          activeOpacity={1}
          onPress={onClose}
          testID="modal-backdrop"
        />

        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.keyboardAvoidingContainer}
          testID="keyboard-avoiding-view"
        >
          <View
            style={[
              styles.bottomSheet,
              {
                backgroundColor: theme.colors.surface,
                maxHeight: SCREEN_HEIGHT * height,
              },
            ]}
            testID="bottom-sheet"
          >
            {/* Header */}
            <Appbar.Header
              style={[styles.header, { backgroundColor: theme.colors.primary }]}
            >
              <Appbar.BackAction onPress={onClose} color="white" />
              <Appbar.Content title={title} color="white" />
            </Appbar.Header>

            {/* Content */}
            <View style={styles.contentContainer}>
              {children}
            </View>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(17, 28, 45, 0.4)',
    justifyContent: 'flex-end',
  },
  backdrop: {
    flex: 1,
  },
  keyboardAvoidingContainer: {
    justifyContent: 'flex-end',
  },
  bottomSheet: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    shadowColor: '#111c2d',
    shadowOffset: { width: 0, height: -12 },
    shadowOpacity: 0.06,
    shadowRadius: 40,
    elevation: 4,
  },
  header: {
    height: 56,
    paddingHorizontal: 8,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  contentContainer: {
    flexShrink: 1,
  },
});

export default BaseBottomSheet;
